import type { CSSProperties } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { InvalidUrl } from '@/components/InvalidUrl';
import { useSignageConfig } from '@/config/ConfigProvider';
import { PreviewBadge } from '@/components/PreviewBadge';
import { useDocumentTheme } from '@/hooks/useDocumentTheme';
import { MqttProvider } from '@/mqtt/MqttProvider';
import { SignageRoute } from '@/routes/SignageRoute';

/**
 * Router shell and theme root — the port of legacy `App.vue`. There is no navigation in
 * this app — a display boots straight into one URL and stays there — so the two routes
 * below are the whole surface.
 *
 * The legacy router declared `/:signage/` with a trailing slash; react-router matches with
 * and without it, so one route covers both.
 *
 * `MqttProvider` (SYM-489) sits inside the config context and above the router outlet: it
 * has to be mounted for the whole life of the page, and its connect gate needs the widgets
 * below it to have registered their topics first. React runs child effects before parent
 * effects, which is exactly the order that requires.
 */
export function App() {
  const config = useSignageConfig();
  useDocumentTheme(config);

  const { topSpacing, botomSpacing } = config.layout;

  return (
    <MqttProvider>
      <BrowserRouter>
      <PreviewBadge />
        {/*
         * Legacy `.signange-container` [sic], renamed — the typo was internal and no config
         * references it. Its padding came from `v-bind()` in SCSS; here the two values become
         * custom properties on the element, read back by the `.signage-container` rule in
         * `global.css`. `botomSpacing` [sic] is the config key and stays misspelled.
         *
         * The cast is the standard React escape hatch for custom properties: `CSSProperties`
         * has no index signature for them.
         */}
        <div
          className="signage-container"
          style={
            {
              '--signage-pt': `${String(topSpacing)}rem`,
              '--signage-pb': `${String(botomSpacing)}rem`,
            } as CSSProperties
          }
        >
          <Routes>
            <Route path="/:signage" element={<SignageRoute />} />
            <Route path="/:signage/:floor" element={<SignageRoute />} />
            <Route path="*" element={<InvalidUrl />} />
          </Routes>
        </div>
      </BrowserRouter>
    </MqttProvider>
  );
}
